/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { ModelProperty } from '../../engine/model-property';
import { Size } from '../../enum/column-size.enum';
import { NameValue } from '../../tool/name-value';
import { SortStatus } from '../../tool/sort-status.enum';
import { BehaviorSubject } from 'rxjs';
import { IComparator } from '../../interface/icomparator';
import { ITextIcon } from '../../interface/itext-icon';
import { ObjectFactory } from '../../engine/http/factory/object-factory';
import { Operator } from '../../enum/operator.enum';
import { RestService } from '../../engine/http/rest.service';

export class ColumnManager implements IComparator, ITextIcon {
  get sortStatus(): SortStatus {
    return this._sortStatus;
  }

  set sortStatus(value: SortStatus) {
    this._sortStatus = value;
  }

  private _filters: NameValue[] = [];
  get filters(): NameValue[] {
    return this._filters ? this._filters : [];
  }
  set filters(value: NameValue[]) {
    this._filters = value;
    if (value) {
      this.isFiltered = !!value && value.length > 0;
    }
    this.getDefaultValue();
  }

  public property: ModelProperty;
  public headerWidth: number;
  public specificWidth: number;
  public width: number;
  public showOnTable: boolean;
  public showOnPdf : boolean;
  public isFiltered: boolean;
  public isGrouped: boolean;
  public toolsIsOpened: boolean;

  private _sortStatus = SortStatus.no;
  public icon: string;
  public text: string;
  public defaultValueFilter: any;

  public fillInnerHtml: boolean;
  public isFixed: boolean;
  public fixedLeftOffset = 0;

  public currentPosition: number;
  public dataSourceSync = new BehaviorSubject<any[]>([]);

  public constructor(property: ModelProperty) {
    this.property = property;
    this.showOnTable = property.showOnTable;
    this.showOnPdf = property.showOnPdf;
    this.text = property.titleByContext?.table;
    if (!this.text) {
      this.text = property.title;
    }
    this.getColumnWidth();
    this.detectInnerHtml();
  }

  private getColumnWidth() {
    if (!this.specificWidth && !this.property.specificWidth) {
      switch (this.property.getColumnSize()) {
        case Size.XS:
          this.width = 40;
          break;
        case Size.S:
          this.width = 80;
          break;
        case Size.M:
          this.width = 120;
          break;
        case Size.L:
          this.width = 160;
          break;
        case Size.XL:
          this.width = 240;
          break;
        case Size.XXL:
          this.width = 320;
          break;
        case Size.XXXL:
          this.width = 500;
          break;
      }
    } else if (this.specificWidth !== undefined) {
      this.width = this.specificWidth;
    } else {
      this.width = this.property.specificWidth;
    }
    if (this.headerWidth === undefined) {
      this.headerWidth = this.width;
    }
  }

  /**
   * Return the value of filters corresponding if exist
   */
  public getDefaultValue() {
    const filter = this.filters.find((f) => f.name === Operator.contain);
    this.defaultValueFilter = filter ? filter.value : null;
  }

  public loadDataSource(restService: RestService) {
    if (this.property.src === 'ws') {
      const factory = new ObjectFactory(this.property.ws, this.property.wsKey, [
        this.property.wsLib,
      ]);
      restService.getAll(factory).subscribe((result) => {
        this.dataSourceSync.next(result);
      });
    } else if (this.property.src === 'data') {
      this.dataSourceSync.next(this.property.data);
    }
  }

  addFilter(nameValue: NameValue) {
    if (!this._filters) {
      this._filters = [nameValue];
    } else {
      this._filters.push(nameValue);
    }
  }

  removeFilter(nameValue: NameValue) {
    const index = this._filters.findIndex((f) => f.name === nameValue.name);
    this._filters.splice(index, 1);
  }

  compare(comparant: any): number {
    return this.property.title
      .toLocaleUpperCase()
      .localeCompare(comparant.property.title.toLocaleUpperCase());
  }

  fix(leftOffset: number) {
    this.fixedLeftOffset = leftOffset;
    this.isFixed = true;
  }

  private detectInnerHtml() {
    this.fillInnerHtml =
      this.property.formatter &&
      (this.property.formatter.type === 'link' ||
        this.property.formatter.type === 'color' ||
        this.property.formatter.type === 'icon' ||
        this.property.formatter.type === 'gmaps');
  }
}
